import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import { compareShellNavDirection } from '@src/modules/shell-routing/domain/shellNavConfig.ts';
import { setPendingShellNavDirection } from '@src/modules/shell-routing/infrastructure/shellNavMotion.ts';
import type {
  ShellNavigateGuard,
  ShellNavigationPort,
} from '../../../domain/ports/ShellNavigationPort.ts';

export type RouteHandler = () => void | Promise<void>;

export interface Route {
  path: string;
  handler: RouteHandler;
}

export type NewCorrelationId = () => string;

const ROUTE_ERROR_TOAST = 'No se pudo mostrar el contenido.';

export class Router implements ShellNavigationPort {
  private readonly routes: Route[] = [];
  private readonly navigateGuards: ShellNavigateGuard[] = [];
  private currentPath = '';
  private started = false;
  private onRouteError: ((message: string) => void) | undefined;

  constructor(
    private readonly telemetry: TelemetryPort,
    private readonly newCorrelationId: NewCorrelationId,
  ) {}

  /** Optional UI sink for handler failures (wired from composition root). */
  setRouteErrorHandler(handler: ((message: string) => void) | undefined): void {
    this.onRouteError = handler;
  }

  addRoute(path: string, handler: RouteHandler): void {
    this.routes.push({ path, handler });
  }

  addNavigateGuard(guard: ShellNavigateGuard): () => void {
    this.navigateGuards.push(guard);
    return () => {
      const i = this.navigateGuards.indexOf(guard);
      if (i >= 0) this.navigateGuards.splice(i, 1);
    };
  }

  navigate(path: string): void {
    const fromPath = this.currentPath || globalThis.location.pathname;
    if (path !== fromPath && !this.runNavigateGuards(path, fromPath)) return;
    setPendingShellNavDirection(compareShellNavDirection(fromPath, path));
    globalThis.history.pushState({}, '', path);
    void this.handleRoute();
    globalThis.dispatchEvent(new CustomEvent('navigate', { detail: { path } }));
  }

  start(): void {
    if (this.started) {
      void this.handleRoute();
      return;
    }
    this.started = true;
    void this.handleRoute();
    globalThis.addEventListener('popstate', () => {
      const path = globalThis.location.pathname;
      const fromPath = this.currentPath;
      if (path !== fromPath && !this.runNavigateGuards(path, fromPath)) {
        globalThis.history.pushState({}, '', fromPath || path);
        return;
      }
      setPendingShellNavDirection('back');
      void this.handleRoute();
    });
    document.addEventListener('click', (e) => this.onDocumentClick(e));
  }

  getCurrentPath(): string {
    return this.currentPath;
  }

  private onDocumentClick(e: Event): void {
    try {
      const target = (e.target as HTMLElement).closest('a');
      if (!target?.href?.startsWith(globalThis.location.origin)) return;
      e.preventDefault();
      let path: string;
      try {
        path = new URL(target.href).pathname;
      } catch (err) {
        this.safeLog('router.linkClick', err, { href: target.href }, 'router: invalid internal URL');
        return;
      }
      this.navigate(path);
    } catch (err) {
      this.safeLog('router.linkClick.fatal', err);
    }
  }

  private runNavigateGuards(path: string, fromPath: string): boolean {
    if (path === fromPath) return true;
    for (let i = this.navigateGuards.length - 1; i >= 0; i--) {
      try {
        if (!this.navigateGuards[i](path, fromPath)) return false;
      } catch (err) {
        this.safeLog(
          'router.navigateGuard',
          err,
          { path, fromPath },
          err instanceof Error ? err.message : 'navigate guard',
        );
        return false;
      }
    }
    return true;
  }

  private matchRoute(path: string): Route | undefined {
    return this.routes.find(
      (r) => r.path === path || (r.path.endsWith('*') && path.startsWith(r.path.slice(0, -1))),
    );
  }

  private async handleRoute(): Promise<void> {
    try {
      const path = globalThis.location.pathname;
      this.currentPath = path;
      const route = this.matchRoute(path);
      if (!route) return;
      try {
        await route.handler();
      } catch (error) {
        this.safeLog('router', error, { path });
        try {
          this.onRouteError?.(ROUTE_ERROR_TOAST);
        } catch {
          void 0;
        }
      }
    } catch (e) {
      this.safeLog('router.fatal', e);
      try {
        this.onRouteError?.(ROUTE_ERROR_TOAST);
      } catch {
        void 0;
      }
    }
  }

  /** Telemetry must never break navigation (popstate / link clicks). */
  private safeLog(
    source: string,
    error: unknown,
    extra: Record<string, unknown> = {},
    errorArg?: Error | string,
  ): void {
    try {
      const payload = errorArg ?? (error instanceof Error ? error : String(error));
      this.telemetry.logError(payload, {
        correlationId: this.newCorrelationId(),
        source,
        error,
        ...extra,
      });
    } catch {
      void 0;
    }
  }
}
